import { InfoCircledIcon } from '@radix-ui/react-icons';

import type { ThemeTabType } from '../../../../entities/post/models';

import { ChildrenCell } from '../ui/children-cell';
import { getThemeIcon } from '../utils/drilldown.utils';

import { METRIC, type StatisticData, type ThemeStatisticData } from '@/entities/insight';
import type { TableColumnConfig } from '@/shared/components/data-table';
import { Badge } from '@/shared/components/shadcn/badge';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils/cn';

// Theme Level Columns
export function getThemeLevelColumns(
  selectedTab: ThemeTabType,
  metric: METRIC,
  onDrillDown?: (row: ThemeStatisticData) => void,
): TableColumnConfig<ThemeStatisticData>[] {
  const indexIcon = getThemeIcon(selectedTab);

  return [
    {
      key: '__index__',
      header: indexIcon,
      size: '40px',
      cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex;
        const pageSize = table.getState().pagination.pageSize;
        const rowModel = table.getRowModel();
        const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
        const actualIndex = pageIndex * pageSize + visualIndex + 1;
        return (
          <div className="text-foreground flex items-center justify-center text-sm font-normal">
            {actualIndex}
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Theme',
      size: '1fr',
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm leading-5 font-medium">{getValue()}</div>
      ),
    },
    {
      key: 'sentiment',
      header: 'Sentiment',
      size: '128px',
      cell: ({ getValue }) => {
        const sentiment = getValue() as string;
        const isPositive = sentiment === 'positive';
        const isNegative = sentiment === 'negative';

        return (
          <Badge
            className={cn(
              'rounded-full px-2.5 py-0.5 text-xs font-semibold text-white',
              isPositive && 'bg-[#2a9d90]',
              isNegative && 'bg-[#dc2626]',
              !isPositive && !isNegative && 'bg-muted-foreground',
            )}
          >
            {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
          </Badge>
        );
      },
    },
    {
      key: metric === METRIC.MENTION ? 'mention_volume' : 'post_volume',
      header:
        metric === METRIC.MENTION ? (
          <TooltipFlat
            triggerClassName="flex items-center gap-1.5"
            contentClassName="w-[384px]"
            content={
              <>
                Mentions are the total number of times this theme appears across all posts <br />
                <br />
                💡 Use this to understand how frequently a topic is discussed, not just how many
                posts mention it.
              </>
            }
          >
            <span>💬</span>
            <span>Mention</span>
            <InfoCircledIcon className="h-4 w-4" />
          </TooltipFlat>
        ) : (
          'Volume'
        ),
      size: '140px',
      enableSorting: true,
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm font-normal">{getValue()}</div>
      ),
    },
    {
      key: 'children',
      header: (
        <div className="flex items-center gap-2">
          <span>🏷️</span>
          <span>Brands mentioned</span>
        </div>
      ),
      size: '240px',
      enableSorting: true,
      accessorFn: (row: ThemeStatisticData) => (row.children ? row.children.length : 0),
      cell: ({ row }) => {
        const rowData = row.original as ThemeStatisticData;
        const children = rowData.children;
        const hasChildren = children && children.length > 0;

        return (
          <ChildrenCell
            children={children}
            onDrillDown={hasChildren && onDrillDown ? () => onDrillDown(rowData) : undefined}
            isDisabled={!hasChildren || !onDrillDown}
            itemType="brand"
            className="w-full"
          />
        );
      },
    },
  ];
}

// Brand Level Columns
export function getBrandLevelColumns(
  metric: METRIC,
  onDrillDown?: (row: StatisticData) => void,
): TableColumnConfig<StatisticData>[] {
  return [
    {
      key: '__index__',
      header: '🏷️',
      size: '40px',
      cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex;
        const pageSize = table.getState().pagination.pageSize;
        const rowModel = table.getRowModel();
        const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
        const actualIndex = pageIndex * pageSize + visualIndex + 1;
        return (
          <div className="text-foreground flex items-center justify-center text-sm font-normal">
            {actualIndex}
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Brand',
      size: '1fr',
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm leading-5 font-medium">{getValue()}</div>
      ),
    },
    {
      key: metric === METRIC.MENTION ? 'mention_volume' : 'post_volume',
      header:
        metric === METRIC.MENTION ? (
          <TooltipFlat
            asChild
            contentClassName="w-[384px]"
            content="Mentions are the total number of times this brand appears across all posts"
          >
            <div className="flex items-center gap-1.5">
              <span>💬</span>
              <span>Mention</span>
              <InfoCircledIcon className="h-4 w-4" />
            </div>
          </TooltipFlat>
        ) : (
          'Volume'
        ),
      size: '140px',
      enableSorting: true,
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm font-normal">{getValue()}</div>
      ),
    },
    {
      key: 'children',
      header: (
        <div className="flex items-center gap-1.5">
          <span>📦</span>
          <span>Products mentioned</span>
        </div>
      ),
      size: '240px',
      enableSorting: true,
      accessorFn: (row: StatisticData) => (row.children ? row.children.length : 0),
      cell: ({ row }) => {
        const rowData = row.original as StatisticData;
        const children = rowData.children;
        const hasChildren = children && children.length > 0;

        return (
          <ChildrenCell
            children={children}
            onDrillDown={hasChildren && onDrillDown ? () => onDrillDown(rowData) : undefined}
            isDisabled={!hasChildren || !onDrillDown}
            itemType="product"
            className="w-full"
          />
        );
      },
    },
  ];
}

// Product Level Columns
export function getProductLevelColumns(metric: METRIC): TableColumnConfig<StatisticData>[] {
  return [
    {
      key: '__index__',
      header: '📦',
      size: '40px',
      cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex;
        const pageSize = table.getState().pagination.pageSize;
        const rowModel = table.getRowModel();
        const visualIndex = rowModel.rows.findIndex((r) => r.id === row.id);
        const actualIndex = pageIndex * pageSize + visualIndex + 1;
        return (
          <div className="text-foreground flex items-center justify-center text-sm font-normal">
            {actualIndex}
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Product',
      size: '1fr',
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm leading-5 font-medium">{getValue()}</div>
      ),
    },
    {
      key: metric === METRIC.MENTION ? 'mention_volume' : 'post_volume',
      header:
        metric === METRIC.MENTION ? (
          <TooltipFlat
            asChild
            contentClassName="w-[384px]"
            content="Mentions are the total number of times this product appears across all posts"
          >
            <div className="flex items-center gap-1.5">
              <span>💬</span>
              <span>Mention</span>
              <InfoCircledIcon className="h-4 w-4" />
            </div>
          </TooltipFlat>
        ) : (
          'Volume'
        ),
      size: '140px',
      enableSorting: true,
      cell: ({ getValue }) => (
        <div className="text-foreground text-sm font-normal">{getValue()}</div>
      ),
    },
  ];
}
